/** @file The AutoTest Store module to request and update tests, runs and results with the server. */
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { object } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as models from '@/models';
import { RubricsStore } from '@/store';
import * as utils from '@/utils';
import { eitherAsyncUtils } from '@/utils';
import { AssignmentsStore } from './assignments';
import { FileTreeStore } from './file_trees';
import { SubmissionsStore } from './submissions';
const storeBuilder = getStoreBuilder();
/**
 * Function to get the initial state of the store module.
 *
 * @returns The initial state of the store module.
 */
const makeInitialState = () => ({
    tests: {},
    testsByAssignment: {},
    resultByRun: {},
    runs: {},
    loaders: {
        testsByAssignmentId: {},
        results: {},
        runs: {},
        resultsBySubmissionId: {},
    },
});
const moduleBuilder = storeBuilder.module('autotest', makeInitialState());
/** The AutoTest store module. */
export var AutoTestStore;
(function (AutoTestStore) {
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    function _stateGetter(key) {
        return (state) => object.getItem(state[key]);
    }
    // eslint-disable-next-line no-underscore-dangle
    const _getTest = _stateGetter('tests');
    // eslint-disable-next-line no-underscore-dangle
    const _getTestByAssignment = _stateGetter('testsByAssignment');
    // eslint-disable-next-line no-underscore-dangle
    const _getRun = _stateGetter('runs');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.getRun = moduleBuilder.read(_getRun, 'getRun');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.getTest = moduleBuilder.read(_getTest, 'getTest');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.getLatestResults = moduleBuilder.read(state => {
        const getter = object.getItem(state.resultByRun);
        return (test) => test.run
            .chain(run => getter(run.id))
            .map(resultByRun => resultByRun.latest.map(id => resultByRun.byId[id]));
    }, 'getLatestResults');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.getResultById = moduleBuilder.read(state => {
        const getter = object.getItem(state.resultByRun);
        return (runId, resultId) => getter(runId).chainNullable(resultsByRun => resultsByRun.byId[resultId]);
    }, 'getResultById');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.getResultBySubmission = moduleBuilder.read(state => {
        const getter = object.getItem(state.resultByRun);
        return (runId, submissionId) => getter(runId).chainNullable(resultsByRun => {
            const id = resultsByRun.bySubmission[submissionId];
            if (id == null) {
                return null;
            }
            return resultsByRun.byId[id];
        });
    }, 'getResultBySubmission');
    /** Adds a loader promise to a specified loader cache. */
    const commitAddLoader = moduleBuilder.commit((state, data) => {
        utils.vueSet(state.loaders[data.loader], data.id, data.value);
    }, 'commitAddLoader');
    const commitRemoveLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, data) => {
        const obj = state.loaders[data.loader];
        utils.vueDelete(obj, data.id);
    }, 'commitRemoveLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    function storeLoader(data) {
        const loaders = data.state.loaders[data.loaderKey];
        return {
            loader: object.getItem(loaders, data.id),
            addLoader: (value) => commitAddLoader({ loader: data.loaderKey, id: data.id, value }),
            removeLoader: () => commitRemoveLoader({ loader: data.loaderKey, id: data.id }),
        };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const deleteAutoTestFromState = (state, autoTest) => {
        utils.vueDelete(state.tests, autoTest.id);
        utils.vueDelete(state.testsByAssignment, autoTest.assignmentId);
        autoTest.runs.forEach(run => {
            utils.vueDelete(state.runs, run.id);
            utils.vueDelete(state.resultByRun, run.id);
        });
    };
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    function createAutoTestResult(state, { result, test, runId, }) {
        const maybeOldObj = object
            .getItem(state.resultByRun, runId)
            .chainNullable(resultState => resultState.byId[result.id]);
        const newFromServer = models.AutoTestResult.fromServerData(result, test.id, runId);
        const newObj = maybeOldObj.caseOf({
            Just: oldObj => newFromServer.amend(oldObj),
            Nothing: () => newFromServer,
        });
        /**
         * Call SubmissionStore.loadSingleSubmission with arguments in this context.
         *
         * @param force Pass true to send a fresh new request when true.
         */
        const loadSubmission = (force) => {
            utils.loadBackgroundPromises(SubmissionsStore.loadSingleSubmission({
                submissionId: newObj.submissionId,
                force,
            }), RubricsStore.loadResult({
                submissionId: newObj.submissionId,
                assignmentId: test.assignmentId,
                force,
            }));
        };
        maybeOldObj.ifJust(oldObj => {
            if (newObj.finished && !oldObj.finished) {
                loadSubmission(true);
            }
        });
        newObj.maybeSubmission.ifNothing(() => loadSubmission(false));
        // Update the AT file tree if we got an extended result and if we
        // retrieved new suites with files.
        if (result instanceof api.ExtendedAutoTestResult) {
            const oldSuitesWithFiles = maybeOldObj.chain(object.attr('suitesWithFiles'));
            const newSuiteFiles = newObj.suitesWithFiles.orDefaultLazy(() => new Set());
            if (oldSuitesWithFiles
                .map(utils.setEq(newSuiteFiles))
                .map(utils.not)
                .orDefault(newSuiteFiles.size > 0)) {
                FileTreeStore.updateAutoTestTree({
                    assignmentId: test.assignmentId,
                    submissionId: newObj.submissionId,
                    autoTest: test,
                    autoTestTree: result.suiteFiles,
                });
            }
        }
        return newObj;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const addAutoTestRunToState = (state, test, run) => {
        const runModel = models.AutoTestRun.fromServerData(run, test.id);
        utils.vueSet(state.runs, run.id, runModel);
        let newResults = [];
        if (run instanceof api.ExtendedAutoTestRun) {
            newResults = run.results.map(result => createAutoTestResult(state, { result, test, runId: runModel.id }));
        }
        utils.vueSet(state.resultByRun, run.id, {
            latest: newResults.map(result => result.id),
            byId: object.makeLookupMapping(newResults, 'id'),
            bySubmission: object.mapToObject(newResults, res => [res.submissionId, res.id]),
        });
        return runModel;
    };
    const commitAutoTest = moduleBuilder.commit((state, data) => {
        const autoTestModel = data.autoTestModel ?? models.AutoTest.fromServerData(data.data);
        deleteAutoTestFromState(state, autoTestModel);
        utils.vueSet(state.tests, autoTestModel.id, autoTestModel);
        utils.vueSet(state.testsByAssignment, autoTestModel.assignmentId, autoTestModel);
        if (data.data != null) {
            data.data.runs.forEach(run => {
                addAutoTestRunToState(state, autoTestModel, run);
            });
        }
    }, 'commitAutoTest');
    const commitAutoTestRun = moduleBuilder.commit((state, { autoTest, run, }) => {
        const runModel = addAutoTestRunToState(state, autoTest, run);
        const test = autoTest.addRun(runModel);
        utils.vueSet(state.tests, autoTest.id, test);
        utils.vueSet(state.testsByAssignment, autoTest.assignmentId, test);
    }, 'commitAutoTestRun');
    const commitDeleteAutoTest = moduleBuilder.commit(deleteAutoTestFromState, 'commitDeleteAutoTest');
    const commitAddResults = moduleBuilder.commit((state, { run, results }) => {
        object.getItem(state.resultByRun, run.id).ifJust(resultsByRun => {
            results.forEach(result => {
                utils.vueSet(resultsByRun.byId, result.id, result);
                utils.vueSet(resultsByRun.bySubmission, result.submissionId, result.id);
            });
        });
    }, 'commitAddResults');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.commitClearAutoTests = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearAutoTests');
    /** Action to delete auto test. */
    AutoTestStore.deleteAutoTest = moduleBuilder.dispatch(async (_, { autoTest, }) => {
        const response = await client.p.autoTest.delete({ autoTestId: autoTest.id });
        SubmissionsStore.commitClearForAssignment({ assignmentId: autoTest.assignmentId });
        RubricsStore.commitClearAssignmentResults({ assignmentId: autoTest.assignmentId });
        await RubricsStore.loadRubric({ assignmentId: autoTest.assignmentId, force: true });
        return response.addCallbacks({
            /** Commit mutation and call the assignment module to update the assignment. */
            onAfterSuccess() {
                commitDeleteAutoTest(autoTest);
                AssignmentsStore.commitUpdateAssignment({
                    assignmentId: autoTest.assignmentId,
                    update: { autoTestId: Maybe.empty() },
                });
            },
        });
    }, 'deleteAutoTest');
    /** Action to update auto test. */
    AutoTestStore.updateAutoTest = moduleBuilder.dispatch(async (_, data) => {
        const res = await client.p.autoTest.patch({
            autoTestId: data.autoTestId,
            requestBody: data.autoTestProps,
        });
        commitAutoTest({ data: res.body });
        return res;
    }, 'updateAutoTest');
    /** Action to load an assignment's autotest. */
    AutoTestStore.loadAutoTestByAssignment = moduleBuilder.dispatch(({ state }, { assignmentId, force = false }) => utils.loadApiWithMaybeForce({
        ...storeLoader({
            id: assignmentId,
            loaderKey: 'testsByAssignmentId',
            state,
        }),
        value: _getTestByAssignment(state)(assignmentId),
        force,
        load: async () => {
            const res = await client.p.assignment.getAutoTest({ assignmentId });
            const result = res.map(models.AutoTest.fromServerData);
            commitAutoTest({ data: result.body, autoTestModel: result.cgResult });
            return result;
        },
    }), 'loadAutoTestByAssignment');
    /** Action to load the content of an auto test. */
    AutoTestStore.loadAutoTest = moduleBuilder.dispatch(async ({ state }, { autoTestId, assignmentId, force = false }) => utils.loadApiWithMaybeForce({
        ...storeLoader({
            id: assignmentId,
            loaderKey: 'testsByAssignmentId',
            state,
        }),
        value: _getTest(state)(autoTestId),
        force,
        load: async () => {
            const res = await client.p.autoTest.get({
                autoTestId,
            });
            const result = res.map(models.AutoTest.fromServerData);
            commitAutoTest({ data: result.body, autoTestModel: result.cgResult });
            return result;
        },
    }), 'loadAutoTest');
    /**
     * Action to reload auto test and rubric.
     *
     * @param autoTest The target auto test to reload.
     * @returns A promise that resolves when the operation is completed.
     */
    const reloadAutoTestAndRubric = (autoTest) => Promise.all([
        AutoTestStore.loadAutoTest({
            autoTestId: autoTest.id,
            assignmentId: autoTest.assignmentId,
            force: true,
        }),
        RubricsStore.loadRubric({ assignmentId: autoTest.assignmentId, force: true }),
    ]);
    /** Action to create a auto test run. */
    AutoTestStore.createAutoTestRun = moduleBuilder.dispatch(async (_, data) => {
        const response = await client.p.autoTest.startRun({
            autoTestId: data.autoTest.id,
        });
        const run = response.cgResult;
        if (run instanceof api.AutoTestRun) {
            commitAutoTestRun({ autoTest: data.autoTest, run });
        }
        return response;
    }, 'createAutoTestRun');
    /** Action to load run(s) for an auto test. */
    AutoTestStore.loadAutoTestRun = moduleBuilder.dispatch((ctx, { autoTest, force = false }) => {
        const oldRun = autoTest.run.throwOnEmpty(new Error('AutoTest has not been run yet.'));
        return utils.loadApiWithMaybeForce({
            ...storeLoader({
                state: ctx.state,
                loaderKey: 'runs',
                id: oldRun.id,
            }),
            value: Maybe.fromPredicate(object.attr('isExtended'), oldRun),
            force,
            load: async () => {
                const response = await client.p.autoTest.getRun({
                    autoTestId: autoTest.id,
                    runId: oldRun.id,
                });
                const augmentedResponse = response.map(body => {
                    commitAutoTestRun({ autoTest, run: response.body });
                    return _getRun(ctx.state)(body.id).unsafeCoerce();
                });
                return augmentedResponse;
            },
        });
    }, 'loadAutoTestRun');
    /** Action to create an auto test set. */
    AutoTestStore.createAutoTestSet = moduleBuilder.dispatch(async (_, { autoTest }) => {
        const res = await client.p.autoTest.addSet({
            autoTestId: autoTest.id,
        });
        const newRes = res.map(newSet => autoTest.addSet(newSet));
        commitAutoTest({ autoTestModel: newRes.cgResult });
        return newRes;
    }, 'createAutoTestSet');
    /** Action to delete auto test set. */
    AutoTestStore.deleteAutoTestSet = moduleBuilder.dispatch(async (_, { autoTest, set }) => {
        const res = await eitherAsyncUtils.toPromise(set.delete());
        const deleteRes = res
            .map(() => autoTest.removeSet(set))
            .addCallbacks({
            /** Commit new data to update the store module. */
            onAfterSuccess() {
                commitAutoTest({ autoTestModel: this.cgResult });
            },
        });
        const reloadRes = await RubricsStore.loadRubric({
            assignmentId: autoTest.assignmentId,
            force: true,
        });
        return [deleteRes, reloadRes];
    }, 'deleteAutoTestSet');
    /** Action to save an auto test set. */
    AutoTestStore.saveAutoTestSet = moduleBuilder.dispatch(async (_, { autoTest, set }) => {
        const res = await eitherAsyncUtils.toPromise(set.save());
        const saveRes = res.map(newSet => autoTest.replaceSet(newSet));
        commitAutoTest({ autoTestModel: saveRes.cgResult });
        const reloadRes = await RubricsStore.loadRubric({
            assignmentId: autoTest.assignmentId,
            force: true,
        });
        return [saveRes, reloadRes];
    }, 'saveAutoTestSet');
    /** Action to delete an auto test suite. */
    AutoTestStore.deleteAutoTestSuite = moduleBuilder.dispatch(async (_, { autoTest, suite }) => {
        const deleteRes = await eitherAsyncUtils.toPromise(suite.delete());
        const loadRes = await reloadAutoTestAndRubric(autoTest);
        return [...loadRes, deleteRes];
    }, 'deleteAutoTestSuite');
    /** Action to save an auto test suite. */
    AutoTestStore.saveAutoTestSuite = moduleBuilder.dispatch(async (_, { autoTest, suite }) => {
        const saveRes = await suite.save();
        const loadRes = await reloadAutoTestAndRubric(autoTest);
        return [...loadRes, saveRes];
    }, 'saveAutoTestSuite');
    const loadAutoTestBySubmission = moduleBuilder.dispatch(async ({ state }, { autoTest, submission, force = true }) => {
        /**
         * Load auto test run data.
         *
         * @param run The auto test run.
         * @returns Promise that resolves with an ApiResult with auto test run result.
         */
        async function loadWithData(run) {
            const res = await client.p.autoTest.getResultBySubmission({
                autoTestId: autoTest.id,
                runId: run.id,
                submissionId: submission.id,
            });
            return res.map(body => {
                const result = createAutoTestResult(state, {
                    result: body,
                    test: autoTest,
                    runId: run.id,
                });
                commitAddResults({ results: [result], run });
                return result;
            });
        }
        return await autoTest.run.caseOf({
            Nothing: async () => {
                throw new Error('AutoTest has no run');
            },
            Just: async (run) => {
                const value = AutoTestStore.getResultBySubmission()(run.id, submission.id);
                return await utils.loadApiWithMaybeForce({
                    ...storeLoader({
                        state,
                        loaderKey: 'resultsBySubmissionId',
                        id: submission.id,
                    }),
                    value,
                    force,
                    load: () => loadWithData(run),
                });
            },
        });
    }, 'loadAutoTestBySubmission');
    /** Action to load auto test result. */
    AutoTestStore.loadAutoTestResult = moduleBuilder.dispatch(async ({ state }, data) => {
        const { autoTestId, submission, assignmentId, force = false } = data;
        /**
         * Get result of an auto test and commit the result to the store module.
         *
         * @param test The target auto test.
         * @param run The target run.
         * @param result The target result to load data for.
         *
         * @returns A promise resolves with an ApiResult with the auto test result.
         */
        async function loadWithData(test, run, result) {
            const response = await client.p.autoTest.getResult({
                autoTestId: test.id,
                resultId: result.id,
                runId: result.runId,
            });
            return response.map(body => {
                const testResult = createAutoTestResult(state, {
                    result: body,
                    test,
                    runId: result.runId,
                });
                commitAddResults({
                    results: [testResult],
                    run,
                });
                return testResult;
            });
        }
        const autoTestLoader = data.loadAutoTestByAssignment
            ? AutoTestStore.loadAutoTestByAssignment({ assignmentId, force: false })
            : AutoTestStore.loadAutoTest({ autoTestId, assignmentId, force: false });
        const { cgResult: autoTest } = await autoTestLoader;
        return await autoTest.run.caseOf({
            Nothing: async () => {
                throw new Error('The AutoTest has not been run yet');
            },
            Just: async (run) => {
                const { cgResult } = await loadAutoTestBySubmission({
                    autoTest,
                    submission,
                    force: false,
                });
                const extendedResult = Maybe.fromPredicate(x => x.isExtended, cgResult);
                return utils.loadApiWithMaybeForce({
                    ...storeLoader({
                        state,
                        id: cgResult.id,
                        loaderKey: 'results',
                    }),
                    force,
                    value: extendedResult,
                    load: () => loadWithData(autoTest, run, cgResult),
                });
            },
        });
    }, 'loadAutoTestResult');
    /** Action to restart an auto test result. */
    AutoTestStore.restartAutoTestResult = moduleBuilder.dispatch(async ({ state }, { autoTest, autoTestResultId }) => await autoTest.run.caseOf({
        Nothing: async () => {
            throw new Error('AutoTest has no run');
        },
        Just: async (run) => {
            const responseRestart = await client.p.autoTest.restartResult({
                autoTestId: autoTest.id,
                runId: run.id,
                resultId: autoTestResultId,
            });
            const responseCreate = responseRestart.map(body => createAutoTestResult(state, {
                result: body,
                test: autoTest,
                runId: run.id,
            }));
            commitAddResults({
                run,
                results: [responseCreate.cgResult],
            });
            return responseCreate;
        },
    }), 'restartAutoTestResult');
    /** Action to clear an auto test run. */
    AutoTestStore.clearAutoTestRun = moduleBuilder.dispatch(async (_, { autoTest }) => {
        const loadAT = AutoTestStore.loadAutoTest({
            autoTestId: autoTest.id,
            assignmentId: autoTest.assignmentId,
            force: true,
        });
        RubricsStore.commitClearAssignmentResults({ assignmentId: autoTest.assignmentId });
        SubmissionsStore.commitClearForAssignment({ assignmentId: autoTest.assignmentId });
        return await loadAT;
    }, 'clearAutoTestRun');
    /** Action to delete auto test results. */
    AutoTestStore.deleteAutoTestResults = moduleBuilder.dispatch(async (_, { autoTest }) => {
        const emptyResponse = api.ApiResult.fromResponseAndValue(new Response(), null);
        return autoTest.run.caseOf({
            Nothing: async () => [emptyResponse],
            Just: async (run) => {
                let response;
                try {
                    response = await client.p.autoTest.stopRun({
                        autoTestId: autoTest.id,
                        runId: run.id,
                    });
                }
                catch (caught) {
                    if (caught instanceof api.ApiResultError &&
                        caught.response.status === 404) {
                        response = emptyResponse;
                    }
                    else {
                        throw caught;
                    }
                }
                const reloadRes = await AutoTestStore.clearAutoTestRun({ autoTest });
                return [response, reloadRes];
            },
        });
    }, 'deleteAutoTestResults');
    /** Action to show or hide the fixture of an auto test. */
    AutoTestStore.toggleFixture = moduleBuilder.dispatch(async (_, { autoTest, fixture }) => {
        await client.p.autoTest[fixture.hidden ? 'showFixture' : 'hideFixture']({
            autoTestId: autoTest.id,
            fixtureId: parseInt(fixture.id, 10),
        });
        const newAutoTest = autoTest.updateFixtures([
            new models.AutoTestFixture(fixture.id, fixture.name, !fixture.hidden),
        ]);
        commitAutoTest({ autoTestModel: newAutoTest });
    }, 'toggleFixture');
    /** Action to create a new auto test for an assignment. */
    AutoTestStore.createAutoTest = moduleBuilder.dispatch(async (_, { assignment }) => {
        const response = await client.p.autoTest.create({
            requestBody: {
                json: {
                    assignmentId: assignment.id,
                    resultsAlwaysVisible: Maybe.of(true),
                    preferTeacherRevision: Maybe.of(false),
                    gradeCalculation: 'full',
                    enableCaching: true,
                },
            },
        });
        return response.addCallbacks({
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            onAfterSuccess() {
                commitAutoTest({ data: this.body });
                AssignmentsStore.commitUpdateAssignment({
                    assignmentId: this.body.assignmentId,
                    update: { autoTestId: Maybe.of(this.body.id) },
                });
            },
        });
    }, 'createAutoTest');
    /** Action to import auto test from a different assignment. */
    AutoTestStore.importAutoTest = moduleBuilder.dispatch(async (_, { srcAssignment, dstAssignment, }) => await srcAssignment.autoTestId.caseOf({
        Nothing: async () => {
            throw new Error('Assignment has no AutoTest');
        },
        Just: async (srcAutoTestId) => {
            const response = await client.p.autoTest.copy({
                autoTestId: srcAutoTestId,
                requestBody: {
                    assignmentId: dstAssignment.id,
                },
            });
            const responseWithAddedCallback = response.addCallbacks({
                /**
                 * Callback to commit and update the store.
                 *
                 * @returns Result of update assignment action.
                 */
                async onAfterSuccess(result) {
                    commitAutoTest({ data: result.body });
                    AssignmentsStore.commitUpdateAssignment({
                        assignmentId: dstAssignment.id,
                        update: { autoTestId: Maybe.of(result.body.id) },
                    });
                },
            });
            SubmissionsStore.commitClearForAssignment({ assignmentId: dstAssignment.id });
            RubricsStore.commitClearAssignmentResults({ assignmentId: dstAssignment.id });
            RubricsStore.loadRubric({ assignmentId: dstAssignment.id, force: true });
            return responseWithAddedCallback;
        },
    }), 'importAutoTest');
    utils.signal.AFTER_USER_CHANGED.registerSync(() => {
        AutoTestStore.commitClearAutoTests();
    });
})(AutoTestStore || (AutoTestStore = {}));
